import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FaYoutube, FaInstagram, FaTiktok } from "react-icons/fa";
import { FiExternalLink, FiSend, FiCopy } from "react-icons/fi";
import { extractUsername, openProfilePopup } from "./ApplicationShared";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

export const PLATFORMS = [
  {
    key: "youtube",   label: "YouTube",   Icon: FaYoutube,   iconColor: "text-red-500",
    statusKey: "youtubeStatus",     urlKey: "youtubeProfileUrl",   codeKey: "youtubeCode",
    emailKey: "youtubeEmail",       codeSentKey: "isYoutubeCodeSend",
    codeVerifiedKey: "isYoutubeCodeVerified", codeSendDateKey: "youtubeCodeSendDate",
    codeVerifDateKey: "youtubeCodeVerificationDate", sendParam: "YouTube",
  },
  {
    key: "instagram", label: "Instagram", Icon: FaInstagram, iconColor: "text-pink-500",
    statusKey: "instagramStatus",   urlKey: "instagramProfileUrl", codeKey: "instagramCode",
    emailKey: null,                 codeSentKey: "isInstagramCodeSend",
    codeVerifiedKey: "isInstagramCodeVerified", codeSendDateKey: "instagramCodeSendDate",
    codeVerifDateKey: "instagramCodeVerificationDate", sendParam: "Instagram",
  },
  {
    key: "tiktok",   label: "TikTok",   Icon: FaTiktok,   iconColor: "text-gray-900",
    statusKey: "tiktokStatus",      urlKey: "tiktokProfileUrl",    codeKey: "tiktokCode",
    emailKey: null,                 codeSentKey: "isTiktokCodeSend",
    codeVerifiedKey: "isTiktokCodeVerified",   codeSendDateKey: "tiktokCodeSendDate",
    codeVerifDateKey: "tiktokCodeVerificationDate", sendParam: "TikTok",
  },
];

const STATUS_CONFIG = {
  inProgress:        { label: "In Progress",        cls: "bg-amber-50 text-amber-600" },
  underVerification: { label: "Under Verification",  cls: "bg-blue-50 text-blue-600" },
  approved:          { label: "Approved",            cls: "bg-green-50 text-green-700" },
  rejected:          { label: "Rejected",            cls: "bg-red-50 text-red-600" },
};

export const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-gray-300 text-xs">—</span>;
  const cfg = STATUS_CONFIG[status] || { label: status, cls: "bg-gray-100 text-gray-600" };
  return <span className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>;
};

const Spinner = ({ sm }) => (
  <div className={`border-2 border-current border-t-transparent rounded-full animate-spin ${sm ? "w-3.5 h-3.5" : "w-4 h-4"}`} />
);

export const formatDateTime = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  return date.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const copyVerificationCode = (code) => {
  navigator.clipboard.writeText(`Creator Hub Verification Code: ${code}`);
  toast.success("Verification code copied");
};

export default function PlatformVerificationCards({ detail, onActionComplete }) {
  const [sendingPlatform, setSendingPlatform] = useState(null);

  const fetchDetailData = async (id) => {
    const token = localStorage.getItem("token");
    const { data } = await axios.get(
      `${baseUrl}/profile-verification-link/details/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!data.status) throw new Error(data.message || "Failed to load details");
    return data.data || {};
  };

  const sendCode = async (id, platform) => {
    setSendingPlatform(platform);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.put(
        `${baseUrl}/profile-verification-link/send-code/${id}`,
        { type: platform },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      if (data.status) {
        toast.success(`${platform} code sent successfully`);
        const updatedDetail = await fetchDetailData(id);
        if (onActionComplete) onActionComplete(updatedDetail, "sendCode");
      } else {
        toast.error(data.message || `Failed to send ${platform} code`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Error sending ${platform} code`);
    } finally {
      setSendingPlatform(null);
    }
  };

  const handleUpdateStatus = async (id, status, type) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${baseUrl}/profile-verification-link/status/${id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ status, type }),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || "Failed to update status");
      toast.success(`${type} status updated to ${status}`);
      const updatedDetail = await fetchDetailData(id);
      if (onActionComplete) onActionComplete(updatedDetail, "updateStatus", status);
    } catch (err) {
      toast.error(err.message || "Error updating status");
    }
  };

  if (!detail) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {PLATFORMS.map(p => {
        const platformStatus = detail[p.statusKey];
        const profileUrl     = detail[p.urlKey];
        const code           = detail[p.codeKey];
        const codeSent       = detail[p.codeSentKey];
        const codeVerified   = detail[p.codeVerifiedKey];
        const codeSendDate   = detail[p.codeSendDateKey];
        const codeVerifDate  = detail[p.codeVerifDateKey];
        const email          = p.emailKey ? detail[p.emailKey] : null;
        const isInProgress   = !platformStatus || platformStatus === "inProgress";
        const isUnderVerif   = platformStatus === "underVerification";

        return (
          <div key={p.key} className="bg-white border border-gray-100 rounded-xl p-4 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p.Icon size={18} className={p.iconColor} />
                <span className="font-semibold text-sm text-gray-800">{p.label}</span>
              </div>
              <StatusBadge status={platformStatus} />
            </div>

            {profileUrl ? (
              <div className="space-y-2.5 text-xs text-gray-600">
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="text-gray-400 font-medium uppercase tracking-wide">Profile URL</div>
                    <button 
                      onClick={(e) => { e.stopPropagation(); openProfilePopup(profileUrl); }} 
                      className="text-[11px] font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 px-2 py-0.5 rounded transition flex items-center gap-1"
                    >
                      View Profile <FiExternalLink size={10} />
                    </button>
                  </div>
                  <div className="text-gray-700 break-all mb-2.5">{profileUrl}</div>
                  <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Username</div>
                  <div className="text-gray-700 break-all">{extractUsername(profileUrl) ? `@${extractUsername(profileUrl)}` : 'Not Available'}</div>
                </div>
                {email && (
                  <div>
                    <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Email</div>
                    <div className="text-gray-700">{email}</div>
                  </div>
                )}
                <div>
                  <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Verification Code</div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-gray-800">{code || "—"}</span>
                    {code && (
                      <button
                        onClick={(e) => { e.stopPropagation(); copyVerificationCode(code); }}
                        title="Copy verification code"
                        className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                      >
                        <FiCopy size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 font-medium uppercase tracking-wide mb-1">Code Sent</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {codeSent === 1 ? (
                      <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">Sent</span>
                    ) : code == null ? (
                      <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Not Sent</span>
                    ) : null}
                    {code != null && (
                      <button
                        onClick={(e) => { e.stopPropagation(); sendCode(detail.platformIds?.[p.key] || detail.id, p.sendParam); }}
                        disabled={sendingPlatform === p.sendParam}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition"
                      >
                        {sendingPlatform === p.sendParam ? <Spinner sm /> : <FiSend size={10} />}
                        {sendingPlatform === p.sendParam ? "Sending..." : "Send Code"}
                      </button>
                    )}
                  </div>
                  {codeSendDate && (
                    <div className="text-gray-400 mt-1">{formatDateTime(codeSendDate)}</div>
                  )}
                </div>
                <div>
                  <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Code Verified</div>
                  {codeVerified === 1 ? (
                    <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">Verified</span>
                  ) : (
                    <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded-full">Not Verified</span>
                  )}
                  {codeVerifDate && (
                    <div className="text-gray-400 mt-1">{formatDateTime(codeVerifDate)}</div>
                  )}
                </div>

                {(isInProgress || isUnderVerif) && (
                  <div className="flex gap-2 pt-2 border-t border-gray-100 flex-wrap">
                    {isInProgress && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleUpdateStatus(detail.platformIds?.[p.key] || detail.id, "underVerification", p.sendParam); }}
                        className="flex-1 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                      >
                        Under Verification
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleUpdateStatus(detail.platformIds?.[p.key] || detail.id, "rejected", p.sendParam); }}
                      className="flex-1 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic">No {p.label} details available</p>
            )}
          </div>
        );
      })}

    </div>
  );
}
